import {
  Button,
  Grid,
  Drawer,
  TextField,
  Autocomplete,
  Box,
} from "@mui/material";
import React, { useEffect, useState,useRef } from "react";
import { Chip, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Popper from "@mui/material/Popper";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
// ASTT-7319 follow-up: per-group collapse/expand for the Recent/Assigned/
// Other grouping, per direct request.
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  getDTRClassDetails,
  getDTRClassDetailsPerRoster,
  getClassRosterDetails,
  getClassRosterIds,
  getRecentRostersForUser,
  getDTRDateDetails,
  getMergeClassTraineeMilestoneDetails,
} from "services/learnPointUserServices";
import ReadOnlyField from "components/Common/ReadOnlyField/ReadOnlyField";
import ClassRosterDetailsTable from "components/LearnPoint/DTR/ClassRosterDetailsTable";
import FieldLabel from "components/LearnPoint/DTR/FieldLabel";
import RosterStatusLegend from "components/LearnPoint/DTR/RosterStatusLegend";
import { getRosterStatusBackground } from "components/LearnPoint/DTR/rosterStatusColors";
import PropTypes from "prop-types";
import BackdropComponent from "components/Spinner/Spinner";
import DisplayProjects from "components/DisplayProjects/DisplayProjects";
import classes from "./ClassDetails.module.css";
import moment from "moment";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import { useLocation } from "react-router-dom";
import applicationUsageLog from "../../../utils/applicationUsagesLog";

const isEditable = true;
// ASTT-7319: Wave/Class Type/Class Status moved to ClassRosterDetailsTable
// (one row per selected roster). Curriculum Status stays here -- it isn't
// part of the new per-roster data source (usp_Lp_GetClassDetailsForDTR_
// PerRoster doesn't cover it; it comes from a different query entirely).
const statusFieldsList = isEditable
  ? [{ label: "Curriculum Status", field: "CurriculumStatus" }]
  : [{ label: "Curriculum Status", field: "curriculumStatus" }];

const dateFieldsList = [
  { label: "TTF Start Date", field: "ttfDateTime" },
  {
    label: "Product Training Start Date",
    field: "productTrainingDateTime",
  },
  { label: "Nesting Start Date", field: "nestingStartDateTime" },
  { label: "Production Start Date", field: "productionStartDate" },
];

// ASTT-7319: Parent Client/Client/Program/Project/Site Location moved to
// ClassRosterDetailsTable. Country stays here -- same reason as
// Curriculum Status above, it isn't part of the new per-roster data.
const cppFieldsList = isEditable
  ? [{ label: "Country", field: "Country" }]
  : [{ label: "Country", field: "country" }];

const datesList = [
  { label: "TTF Start Date", field: "TTFDateTime" },
  { label: "Product Training Start Date", field: "ProductTrainingDateTime" },
  { label: "Nesting Start Date", field: "NestingStartDateTime" },
  { label: "Production Start Date", field: "ProductionStartDate" },
];

// ASTT-7319: getCountFieldsList (TTF/Product Training Day 1 Show, Active
// Head Count, Graduated Head Count, Termed) removed entirely -- all four
// fields moved to ClassRosterDetailsTable, which handles the per-row
// TTFNA distinction itself (see that component's own comment on why a
// single "Day 1 Show" column with a per-row label was the right design,
// now that each selected roster can have its own TTFNA value).

const readinessFieldsList = [
  { label: "Client ID Readiness", field: "ClientIdReadiness", labelLength: 7, fieldLength: 5 },
  { label: "TTEC ID Readiness", field: "TtecIdReadiness", labelLength: 7, fieldLength: 5 },
  { label: "Equipment Readiness", field: "EquipmentReadiness", labelLength: 7, fieldLength: 5 },
];

const formatDate = (date, formatString) => {
  if (date) {
    return moment(date).format(formatString);
  }
  return "";
};

const initialRosterValue = {
  rosterId: "",
  rosterLabel: "",
  rosterStatusId: 0,
};

// ASTT-7319 follow-up: hard cap on Class Roster Ids selection, replacing
// the earlier "Select All" attempt per direct request. Shared with
// renderTags' own limitTags below (previously a separate, independently-
// hardcoded 5) so the visible chip limit and the actual selection limit
// can't drift out of sync with each other.
// ASTT-7319 follow-up: raised from 5 to 20, per direct stakeholder-demo
// feedback, alongside the new Recent/Assigned/Other grouping -- a
// trainer selecting from their own Assigned group especially would
// often need more than 5 at once.
const MAX_SELECTED_ROSTERS = 20;

export const StyledChip = styled(Chip)(() => ({
  borderRadius: "32px",
  maxWidth: 120,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    maxWidth: "100%",
  },
}));

export const LimitChip = styled(Chip)(() => ({
  fontWeight: "bold",
  color: "#3299FF",
}));

export default function DTRClassDetails({
  rosterId,
  setNewClassRosterId,
  isEditable,
  onClassRequestIdChange,
  onJumpToDtrDate,
  dtrRefreshKey,
  reportType,
  isHandOffTabActive,
  dtrDate,
  isTrainingNotesTabActive,
  handOffRefreshKey,
  onRosterStatusListChange,
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  // ASTT-7178: which domain (if any) is currently driving roster status
  // coloring -- "handoff" reads option.handOffStatus (ReportTypeId-based),
  // "trainingNotes" reads option.dtrStatus (DTR-Date-based), null means no
  // coloring at all (Roster/Attendance tabs). Only one can be true at a
  // time in practice (only one tab is active), but this is written so
  // that isn't assumed anywhere else -- handoff always wins if somehow
  // both were true, matching this being the FIRST domain built.
  const activeStatusMode = isHandOffTabActive
    ? "handoff"
    : isTrainingNotesTabActive
      ? "trainingNotes"
      : null;
  const [classDetails, setClassDetails] = useState({});
  const [loading, setLoading] = useState(false);
  // ASTT-7319: one row per selected roster, feeds ClassRosterDetailsTable.
  const [perRosterDetails, setPerRosterDetails] = useState([]);
  const [perRosterLoading, setPerRosterLoading] = useState(false);

const boxRef = useRef(null);
  const [filterRosterData, setFilterRosterData] = useState([]);
  const [rosterDetails, setRosterDetails] = useState([]);
  // ASTT-7319 follow-up: per-group collapse/expand, per direct request --
  // a Set of currently-collapsed group names ("Recent Roster(s)",
  // "Assigned Roster", "Other Rosters"), so a long "Other Rosters" list
  // can be collapsed out of the way to see the other sections without
  // scrolling past it. Starts empty (every group expanded by default,
  // matching the existing behavior before this was added).
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  // ASTT-7319: guards the redirect-prefill effect below so it only ever
  // applies once, on the roster this screen was redirected in with --
  // without this, if the user later clears their own selection, the
  // effect (re-running whenever filterRosterData/rosterId are still
  // truthy) would silently re-apply the original roster right back,
  // making it impossible to actually clear.
  const hasAppliedRedirectRosterRef = useRef(false);
  const Snackhandler = (open, message, severity) => {
    const obj = {
      open: open,
      message: message,
      severity: severity,
    };

    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  const formatString = (text) => String(text).trim();

  const prepareRosterLabel = (item) => {
    let { ClassRosterId, ProjectName, Wave, Trainer, RosterStatus } = item;
    if (ProjectName === "" || !ProjectName) {
      ProjectName = "NA";
    } else {
      ProjectName = formatString(ProjectName);
    }

    if (Wave === "") {
      Wave = "NA";
    } else {
      Wave = formatString(Wave);
    }

    if (Trainer === "") {
      Trainer = "NA";
    } else {
      Trainer = formatString(Trainer);
    }

    if (RosterStatus === "Active") {
      RosterStatus = "IP";
    } else if (RosterStatus === "Cancelled") {
      RosterStatus = "CN";
    } else if (RosterStatus === "Pending Cancellation") {
      RosterStatus = "PC";
    } else if (RosterStatus === "Closed") {
      RosterStatus = "CL";
    } else {
      RosterStatus = formatString(RosterStatus);
    }

    return `${ClassRosterId} - ${ProjectName} - ${Wave} - (${Trainer}) - (${RosterStatus})`;
  };

  const fetchRosterData = async (reportTypeId, dtrDateParam) => {
    setLoading(true);
    try {
      // ASTT-7319 follow-up: personalization groups, per direct
      // stakeholder-demo feedback -- "Assigned Roster" (this user is the
      // Trainer) uses the backend's own FILTER_CLASS_ROSTER_BY_TRAINER_EMAIL
      // endpoint (getClassRosterIds' own second argument), called
      // separately with the current user's email rather than string-
      // matching each roster's own Trainer name field against it -- more
      // reliable, since it's the backend's own authoritative filter, not
      // vulnerable to casing/whitespace/middle-name mismatches a plain
      // name comparison would be.
      //
      // "Recent Roster(s)" (rosters this user has recently drafted/sent
      // Training Notes or Hand-off Report for) now uses
      // getRecentRostersForUser, a new endpoint whose underlying query
      // filters LP_DTRTrainingNotes/LP_HandOffReport's own ModifiedBy
      // column -- that column stores a display name (confirmed via the
      // actual stored procedures, e.g. "Hitesh Kavar"), not an email, so
      // this passes localStorage's "User" entry, not "EmailId" like the
      // Assigned Roster call above uses.
      //
      // ASTT-7178: reportTypeId (Hand Off) and dtrDateParam (Training
      // Notes) are threaded into the same getClassRosterIds call rather
      // than a separate endpoint/SP -- the response's rows then also
      // carry HandOffReportStatus/DTRStatus for whichever was passed.
      // Only one is ever non-null in practice (only one tab active at a
      // time), but passing both through unconditionally keeps this
      // function's own signature and call sites simple.
      const currentUserEmail = localStorage.getItem("EmailId") || "";
      const currentUserName = localStorage.getItem("User") || "";
      const [allRostersResponse, assignedRostersResponse, recentRostersResponse] =
        await Promise.all([
          getClassRosterIds("", reportTypeId, dtrDateParam),
          currentUserEmail ? getClassRosterIds(currentUserEmail, reportTypeId, dtrDateParam) : null,
          currentUserName
            ? getRecentRostersForUser(currentUserName, MAX_SELECTED_ROSTERS)
            : null,
        ]);

      if (allRostersResponse?.data?.statusCode === 200) {
        const rosterDataList = allRostersResponse?.data || [];

        const assignedRosterIds = new Set(
          (assignedRostersResponse?.data?.statusCode === 200
            ? assignedRostersResponse?.data?.data ?? []
            : []
          ).map((item) => String(item?.ClassRosterId)),
        );
        // ASTT-7319 follow-up: no longer empty -- see this function's own
        // opening comment for the field-name (name vs. email) reasoning.
        if (recentRostersResponse?.statusCode !== 200) {
          // ASTT-7319 follow-up: logged explicitly -- confirmed reported
          // as "Recent Roster(s) group doesn't appear at all, even after
          // restarting both projects," which a restart couldn't have
          // fixed if this were genuinely just missing/empty data. Most
          // likely explanations: the procedure doesn't exist yet server-
          // side, or its assumed LP_HandOffReport table name is wrong, or
          // this user's stored ModifiedBy value doesn't exactly match
          // localStorage's "User" entry -- this log shows the actual
          // response so which one it is can be confirmed directly rather
          // than guessed at further.
          console.error(
            "getRecentRostersForUser did not return statusCode 200:",
            recentRostersResponse,
          );
        }
        const recentRosterIds = new Set(
          (recentRostersResponse?.statusCode === 200
            ? recentRostersResponse?.data ?? []
            : []
          ).map((item) => String(item?.ClassRosterId)),
        );

        const prepareRosterData = rosterDataList.data
          .map((item) => {
            const rosterId = item?.ClassRosterId || "Unknown ID";
            let group = "Other Rosters";
            if (recentRosterIds.has(String(rosterId))) {
              group = "Recent Roster(s)";
            } else if (assignedRosterIds.has(String(rosterId))) {
              group = "Assigned Roster";
            }
            return {
              rosterId,
              rosterLabel: prepareRosterLabel(item),
              rosterStatusId: item?.RosterStatusId || 0,
              group,
              // ASTT-7178: only meaningful when the corresponding param
              // was passed to this fetch; defaults to "Pending" rather
              // than undefined either way, so a status lookup never has
              // to separately handle "field absent" vs. "field present
              // but genuinely Pending."
              handOffStatus: item?.HandOffReportStatus || "Pending",
              dtrStatus: item?.DTRStatus || "Pending",
            };
          })
          // ASTT-7319 follow-up: sorted by group first (Recent, then
          // Assigned, then Other), numerically by rosterId within each --
          // MUI's Autocomplete groupBy doesn't sort options into their
          // groups itself, it just labels whatever's already adjacent, so
          // the underlying array has to already be ordered this way for
          // groups to render as clean, separate blocks rather than
          // interleaved.
          .sort((a, b) => {
            const groupOrder = {
              "Recent Roster(s)": 0,
              "Assigned Roster": 1,
              "Other Rosters": 2,
            };
            const groupDiff = groupOrder[a.group] - groupOrder[b.group];
            if (groupDiff !== 0) return groupDiff;
            return (Number(a.rosterId) || 0) - (Number(b.rosterId) || 0);
          });
        setFilterRosterData(prepareRosterData);
      } else {
        Snackhandler(true, "No Rosters Found.", "error");
      }
    } catch (error) {
      Snackhandler(true, "Internal Error Occured", "error");
    }
    setLoading(false);
  };

  const handleRosterChange = async (event, value) => {
    // ASTT-7319 follow-up: "Select All" replaced with a hard cap on how
    // many rosters can be selected at once -- confirmed via direct
    // request that Select All isn't wanted here. MAX_SELECTED_ROSTERS
    // matches the 5-chip limitTags already established in renderTags
    // below, so the visible chip limit and the actual selection limit
    // now agree rather than one silently allowing more than the other
    // visually shows.
    let effectiveValue = value;
    if (effectiveValue.length > MAX_SELECTED_ROSTERS) {
      effectiveValue = effectiveValue.slice(0, MAX_SELECTED_ROSTERS);
      Snackhandler(
        true,
        `You can select up to ${MAX_SELECTED_ROSTERS} rosters at a time.`,
        "warning",
      );
    }

    setRosterDetails(effectiveValue);
    const selectedRosterIds = effectiveValue
      .map((item) => item.rosterId)
      .join(",");
    setNewClassRosterId(selectedRosterIds);

    if (!selectedRosterIds) {
      setClassDetails({});
      setClassRosterDetails({});
      setStatusFields(prepareInitialFields(statusFieldsList));
      setCppFields(prepareInitialFields(cppFieldsList));
      setDateFields(prepareInitialFields(dateFieldsList));
      setDatesFieldList(prepareInitialFields(datesList));
      setReadinessFields(prepareInitialFields(readinessFieldsList));
      // ASTT-7319: reset the new per-roster table data too, on clear.
      setPerRosterDetails([]);
      return;
    }

    try {
      const dtrClassDetailsResponse =
        await getDTRClassDetails(selectedRosterIds);

      if (dtrClassDetailsResponse?.statusCode === 200) {
        setClassDetails(dtrClassDetailsResponse?.data[0]);
        // usp_Lp_GET_ClassDetailsForDTR returns ClassRequestId (comma-list for merged rosters);
        // bubbled up to DtrDetails.js for the attachment upload flow.
        onClassRequestIdChange?.(dtrClassDetailsResponse?.data[0]?.ClassRequestId);
      } else {
        Snackhandler(true, "Failed to fetch class details..", "error");
      }

      // ASTT-7319: new, additive fetch feeding ClassRosterDetailsTable --
      // one row per selected roster, alongside the existing combined
      // classDetails fetch above rather than replacing it (classDetails
      // still feeds Curriculum Status/dates/Country/Readiness, which
      // aren't part of this new per-roster data).
      setPerRosterLoading(true);
      try {
        const perRosterResponse =
          await getDTRClassDetailsPerRoster(selectedRosterIds);
        if (perRosterResponse?.statusCode === 200) {
          setPerRosterDetails(perRosterResponse?.data ?? []);
        } else {
          setPerRosterDetails([]);
        }
      } finally {
        setPerRosterLoading(false);
      }

      const classRosterDetailsResponse =
        await getDTRDateDetails(selectedRosterIds);

      if (classRosterDetailsResponse?.statusCode === 200) {
        setClassDetails(classRosterDetailsResponse?.data[0]);
      } else {
        Snackhandler(true, "Fialed to fetch roster Details...", "error");
      }

      const traineeMilestoneData =
        await getMergeClassTraineeMilestoneDetails(selectedRosterIds);
      if (traineeMilestoneData?.message === "Success") {
        const hasData = Object.values(traineeMilestoneData.data).length > 0;
        if (!hasData) {
          Snackhandler(
            true,
            `No trainee data found for Roster ID: ${selectedRosterIds}`,
            "warning",
          );
        }
      }
    } catch (error) {
      Snackhandler(
        true,
        "An error occurred while fetching the details...",
        "error",
      );
    }
  };

  // ASTT-7319 follow-up: per-group "Select All", per direct
  // stakeholder-demo feedback -- deliberately not offered for "Other
  // Rosters" (only wired in from renderGroup for the other two groups).
  // Merges every roster in the named group into whatever's already
  // selected (rather than replacing the selection outright), then hands
  // off to handleRosterChange itself so the same MAX_SELECTED_ROSTERS
  // cap/warning applies here exactly as it does to any other selection
  // change -- no separate, parallel cap-checking logic to keep in sync.
  const handleSelectAllInGroup = (groupName) => {
    const groupOptions = filterRosterData.filter(
      (option) => option.group === groupName,
    );
    const alreadySelectedIds = new Set(
      rosterDetails.map((r) => r.rosterId),
    );
    const toAdd = groupOptions.filter(
      (option) => !alreadySelectedIds.has(option.rosterId),
    );
    handleRosterChange(null, [...rosterDetails, ...toAdd]);
  };

  // ASTT-7178: keeps already-SELECTED roster objects (rosterDetails,
  // which is what renderTags actually reads via the Autocomplete's value
  // prop) in sync with filterRosterData whenever the latter refreshes --
  // e.g. after a report-type toggle recomputes handOffStatus. Without
  // this, a toggle would correctly recolor the dropdown OPTIONS (which
  // read straight from filterRosterData) while leaving already-selected
  // CHIPS showing whichever handOffStatus they held at selection time.
  // Only swaps in the matching fresh object per already-selected id --
  // never adds or removes a selection itself.
  useEffect(() => {
    if (rosterDetails.length === 0 || filterRosterData.length === 0) return;
    const freshById = new Map(filterRosterData.map((r) => [r.rosterId, r]));
    const refreshed = rosterDetails.map((r) => freshById.get(r.rosterId) ?? r);
    const changed = refreshed.some((r, i) => r !== rosterDetails[i]);
    if (changed) setRosterDetails(refreshed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRosterData]);

  // ASTT-7178: bubbles the full roster status list up to DtrDetails.js,
  // which threads it down to HandOffReport.js -- lets that sibling
  // component proactively disable Save/Send when the CURRENT multi-
  // roster selection includes an already-Sent report, reusing this same
  // fetch (no separate endpoint/call). Runs whenever filterRosterData
  // itself changes, which already covers every case that matters (initial
  // load, report-type/date toggle, and a successful save via
  // dtrRefreshKey/handOffRefreshKey feeding back into fetchRosterData).
  useEffect(() => {
    onRosterStatusListChange?.(filterRosterData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRosterData]);

  // ASTT-7178: re-fetches the roster list (this time WITH whichever
  // status param is relevant) whenever the active domain/tab or its own
  // param changes -- reuses fetchRosterData itself rather than a
  // separate endpoint/call, per direct request, and covers BOTH Hand Off
  // (reportType) and Training Notes (dtrDate) through the same effect.
  // ALSO re-fetches on a successful save (dtrRefreshKey/handOffRefreshKey,
  // bumped by TrainingNotes.js's onDtrSaved / HandOffReport.js's
  // onHandOffSaved respectively) -- without this, a roster's status
  // transition (e.g. Pending -> Draft, or Draft -> Sent) wasn't reflected
  // in the picker's coloring until something else (a toggle) happened to
  // trigger a refetch anyway. Skips the very first render (fetchRosterData
  // already runs once on mount, in the effect below) so switching straight
  // into either tab with its own default param doesn't trigger a
  // redundant second fetch immediately.
  const statusModeMountRef = useRef(true);
  useEffect(() => {
    if (statusModeMountRef.current) {
      statusModeMountRef.current = false;
      return;
    }
    if (activeStatusMode === "handoff" && reportType != null) {
      fetchRosterData(reportType, null);
    } else if (activeStatusMode === "trainingNotes" && dtrDate) {
      fetchRosterData(null, dtrDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatusMode, reportType, dtrDate, dtrRefreshKey, handOffRefreshKey]);

  // ASTT-7319 follow-up: re-fetches everything handleRosterChange fetches
  // for the CURRENTLY-selected roster(s), whenever dtrRefreshKey changes
  // (incremented by DtrDetails.js after a successful Training Notes
  // Save/Send) -- previously the Roster Details table/View DTRs list only
  // ever refreshed on the next manual Class Roster Ids selection change,
  // matching the reported "have to refresh and re-select the roster"
  // workaround. Skips the very first render (dtrRefreshKeyMountRef) since
  // that initial fetch is already handled by the user's own selection or
  // the redirect-prefill effect below -- this only needs to fire on
  // genuine, later changes.
  const dtrRefreshKeyMountRef = useRef(true);
  useEffect(() => {
    if (dtrRefreshKeyMountRef.current) {
      dtrRefreshKeyMountRef.current = false;
      return;
    }
    if (rosterDetails.length > 0) {
      handleRosterChange(null, rosterDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dtrRefreshKey]);

  // ASTT-7319: pre-fills the Class Roster Ids search box with the roster
  // this screen was redirected in with (from DtrList.js's per-row
  // ExitToAppIcon button, via location.state.classRosterId), per direct
  // request -- replacing the separate read-only "Roster ID" label that
  // used to show here instead when isEditable was false for this flow.
  // Waits for filterRosterData to finish loading (the redirect's rosterId
  // needs to be matched against it, not just displayed), and only applies
  // once (hasAppliedRedirectRosterRef) so a later manual clear by the
  // user isn't silently overwritten back to the redirected roster.
  // Reuses handleRosterChange itself, rather than duplicating its own
  // fetch logic, so every side effect a normal manual selection triggers
  // (setNewClassRosterId, the per-roster details fetch, etc.) happens
  // identically here too.
  useEffect(() => {
    if (
      hasAppliedRedirectRosterRef.current ||
      !rosterId ||
      filterRosterData.length === 0
    ) {
      return;
    }
    hasAppliedRedirectRosterRef.current = true;

    const matchingRoster = filterRosterData.find(
      (item) => String(item.rosterId) === String(rosterId),
    );
    if (matchingRoster) {
      handleRosterChange(null, [matchingRoster]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRosterData, rosterId]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        // ASTT-7178: passes whichever param matches the active mode at
        // mount time (both null on Roster/Attendance tabs) -- harmless
        // either way, the unused status field is simply ignored.
        await fetchRosterData(
          activeStatusMode === "handoff" ? reportType : null,
          activeStatusMode === "trainingNotes" ? dtrDate : null,
        );

        if (cancelled) return;

        // ASTT-7319 follow-up: rosterId added to this condition -- it was
        // only checking that location.state existed at all, not that it
        // actually carried a usable classRosterId. On a normal page load
        // with no roster selected yet (location.state present but empty,
        // or simply missing this field), this fired both calls below
        // with an empty/null id anyway, producing real 400s in the
        // console for a completely expected, non-error state.
        if (location?.state && rosterId) {
          // location.state is an object; pull out the actual ID field rather than
          // passing the whole object (which axios would serialize as [object Object]).
          await getClassRosterDetails(location?.state?.classRosterId);

          if (cancelled) return;

          await getDTRDateDetails(rosterId);

          if (cancelled) return;
        }

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "LearnPoint",
            "Class Roster",
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const fieldValue = {
    labelLength: 4,
    labelText: "",
    fieldLength: 8,
    fieldText: "",
  };

  const prepareInitialFields = (sourceList) => {
    return sourceList.map((item) => ({
      ...fieldValue,
      labelText: item?.label,
      field: item?.field,
      // ASTT-7319: pass through if the source list sets these (e.g. a
      // longer label needing a wider column) -- falls back to fieldValue's
      // own existing defaults (4/8), NOT ReadOnlyField's internal default
      // of 6, when a list doesn't set them -- an earlier version of this
      // let it fall through to undefined here, which would have silently
      // shifted every other label column app-wide from 4 to 6.
      labelLength: item?.labelLength ?? fieldValue.labelLength,
      fieldLength: item?.fieldLength ?? fieldValue.fieldLength,
    }));
  };

  const prepareFields = (sourceList, setStateCall) => {
    const updateFieldsData = sourceList.map((item) => {
      if (classDetails?.hasOwnProperty(item?.field)) {
        return { ...item, fieldText: classDetails[item?.field] };
      } else if (classRosterDetails?.hasOwnProperty(item?.field)) {
        return { ...item, fieldText: classRosterDetails[item?.field] };
      }
      return item;
    });

    setStateCall(updateFieldsData);
  };

  const prepareDateFields = (sourceList, setStateCall) => {
    const updateFieldsData = sourceList.map((item) => {
      if (Array.isArray(classDetails[item.field])) {
        const formattedDates = classDetails[item.field]
          .map((date) => formatDate(date, "YYY-MM-DD"))
          .join(",");
        return { ...item, fieldText: formattedDates };
      } else if (classDetails?.hasOwnProperty(item?.field)) {
        return { ...item, fieldText: classDetails[item?.field] };
      } else if (classRosterDetails?.hasOwnProperty(item?.field)) {
        return {
          ...item,
          fieldText: formatDate(classRosterDetails[item?.field], "YYYY-MM-DD"),
        };
      }
      return item;
    });

    setStateCall(updateFieldsData);
  };

  const [classRosterDetails, setClassRosterDetails] = useState();
  const [statusFields, setStatusFields] = useState(
    prepareInitialFields(statusFieldsList),
  );
  const [dateFields, setDateFields] = useState(
    prepareInitialFields(dateFieldsList),
  );
  const [datesFieldList, setDatesFieldList] = useState(
    prepareInitialFields(datesList),
  );
  const [cppFields, setCppFields] = useState(
    prepareInitialFields(cppFieldsList),
  );
  const [readinessFields, setReadinessFields] = useState(
    prepareInitialFields(readinessFieldsList),
  );

  const [projectNames, setProjectNames] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);

  const handleViewProjects = () => {
    setPanelOpen(!panelOpen);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      // ASTT-7319 follow-up: no guard existed here at all -- this ran
      // unconditionally on mount regardless of whether rosterId was
      // actually populated yet, producing a genuine 400 in the console
      // (both calls below sent with an empty id) on a completely normal
      // page load before any roster is selected.
      if (!rosterId) return;

      if (!cancelled) {
        setLoading(true);
      }

      try {
        const dtrClassDetailsResponse = await getDTRClassDetails(rosterId);

        if (cancelled) return;

        if (dtrClassDetailsResponse?.statusCode === 200) {
          if (!cancelled) {
            setClassDetails(dtrClassDetailsResponse?.data[0]);
            // Same reasoning as handleRosterChange above, for the initial-mount fetch
            // path (driven by the rosterId prop rather than the Autocomplete selection).
            onClassRequestIdChange?.(
              dtrClassDetailsResponse?.data[0]?.ClassRequestId,
            );
          }
        }

        const classRosterDetailsResponse =
          await getClassRosterDetails(rosterId);

        if (cancelled) return;

        if (classRosterDetailsResponse?.statusCode === 200) {
          if (!cancelled) {
            setClassRosterDetails(classRosterDetailsResponse?.data);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const updateProjectNames = () => {
    const projects = classDetails?.Project?.split("|");
    if (projects?.length > 1) {
      setProjectNames(projects);
    }
  };

  const isMultiProject = (labelName) =>
    labelName === "Project" && classDetails?.Project?.split("|")?.length > 1;

  useEffect(() => {
    prepareFields(statusFields, setStatusFields);
    prepareDateFields(dateFields, setDateFields);
    prepareDateFields(datesFieldList, setDatesFieldList);
    prepareFields(cppFields, setCppFields);
    // ASTT-7319: the ASTT-7322 TTFNA fix and its own subsequent bug fix
    // that used to live here (getCountFieldsList / countFields) no longer
    // apply -- those four fields moved entirely to
    // ClassRosterDetailsTable, which computes its own per-row TTFNA
    // distinction directly from the new per-roster data, with no
    // equivalent prepareFields/prepareInitialFields step needed here.
    prepareFields(readinessFields, setReadinessFields);
    updateProjectNames();
  }, [classRosterDetails, classDetails]);

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      {/* ASTT-7319 follow-up: no internal collapse control here anymore --
          DtrDetails.js already wraps this whole component in its own
          Accordion titled "Class Details", so an internal toggle here was
          a redundant second collapse mechanism for the same section. The
          Accordion now auto-collapses itself once a roster is selected;
          this component just renders its content plainly. */}
      <Grid container>
        {/* ASTT-7319: xs={12}, was xs={6} -- this was one half of a
            two-column layout; with the other column (cppFields/Country)
            removed, the search box no longer needs to share width with
            anything, so it now spans the full row. */}
        <Grid item xs={12}>
          {/* ASTT-7319: "Selected Rosters" heading removed -- my earlier
              interpretation of "add heading" wasn't right; removed
              directly per follow-up feedback rather than guessed at
              again. */}
          <Grid container>
            <Grid item xs={12}>
              {isEditable ? (
                <Grid container>
                  {/* ASTT-7319: xs={12}/xs={12} (label on top, control
                      below), was xs={4}/xs={8} (side-by-side) -- matches
                      the Successes/Challenges/Topics Covered/Parking Lot/
                      Attachment style used elsewhere. */}
                  {/* ASTT-7319 follow-up: padding removed from the label
                      item (was creating excess gap stacked on top of
                      FieldLabel's own margin, same fix already applied
                      elsewhere); borderRadius on the control below
                      changed from 32 (pill-style) to 4px, matching
                      RichTextEditorField.js's actual radius, per direct
                      request. */}
                  <Grid item xs={12}>
                    {/* ASTT-7178: label stays left-aligned; legend (only
                        rendered while a status mode is active) sits on
                        the same row, pushed to the right -- flexWrap so
                        it drops to its own line rather than overlapping
                        the label on a narrow viewport, per "remain
                        responsive." */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <FieldLabel>
                        Class Roster Ids
                        {/* ASTT-7319 follow-up: visible selected count added --
                            with several chips collapsing behind "+N" (per the
                            existing limitTags), there wasn't otherwise a quick
                            way to tell how many rosters were actually selected
                            without counting chips or opening the dropdown. */}
                        {rosterDetails.length > 0 && ` (${rosterDetails.length} selected)`}
                      </FieldLabel>
                      {activeStatusMode && <RosterStatusLegend />}
                    </Box>
                  </Grid>                 
                  <Grid item xs={12}>
                    <Box
                      ref={boxRef}
                      sx={{
                        // ASTT-7178: was a flat 400 -- widened ~25% (500)
                        // per direct request. Responsive: caps at 500 on
                        // sm+ viewports but fills available width below
                        // that, rather than a fixed value that could
                        // overflow a narrow container.
                        width: { xs: "100%", sm: 500 },
                        // ASTT-7319 follow-up: was "1px solid gray" -- a
                        // visibly heavier/darker shade than every other
                        // control's border (DTR Date/Curriculum Status/
                        // Email Distro all use this lighter #d3d3d3),
                        // which is likely what read as an unwanted
                        // shadow/heavier-border look, per direct request.
                        // Matched to that same consistent shade instead of
                        // removed entirely, since every sibling control
                        // does have a border -- removing this one outright
                        // would have created a different inconsistency.
                        border: "1px solid #d3d3d3",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <Autocomplete
                        multiple
                        options={filterRosterData}
                        value={rosterDetails}
                        getOptionLabel={(item) => item.rosterLabel || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.rosterId === value.rosterId
                        }
                        onChange={handleRosterChange}
                        // ASTT-7319 follow-up: Recent/Assigned/Other
                        // grouping, per direct stakeholder-demo feedback --
                        // groupBy just labels whatever's already adjacent
                        // in filterRosterData (sorted by group in
                        // fetchRosterData above), it doesn't sort options
                        // into groups itself. renderGroup adds a "Select
                        // All" link to the Recent/Assigned group headers
                        // specifically (deliberately absent for "Other
                        // Rosters", per direct request) -- onMouseDown
                        // with preventDefault rather than onClick, since a
                        // plain onClick here can get swallowed by the
                        // Autocomplete's own outside-click/blur handling
                        // before it ever fires.
                        groupBy={(option) => option.group}
                        renderGroup={(params) => {
                          // ASTT-7178: "Select All" only makes sense (and
                          // is only shown) when the group actually has
                          // MORE than one roster to select -- with just
                          // one, it's redundant with simply clicking that
                          // one option directly.
                          const groupOptionCount = filterRosterData.filter(
                            (option) => option.group === params.group,
                          ).length;
                          const canSelectAll =
                            (params.group === "Recent Roster(s)" ||
                              params.group === "Assigned Roster") &&
                            groupOptionCount > 1;
                          // ASTT-7319 follow-up: collapse/expand, per
                          // direct request -- toggled via the whole
                          // header row (not just the chevron icon,
                          // giving a larger, easier click target),
                          // onMouseDown+preventDefault for the same
                          // reason "Select All" above needs it (a plain
                          // onClick here can get swallowed by the
                          // Autocomplete's own outside-click/blur
                          // handling before it ever fires). Select All's
                          // own onMouseDown already stops propagation
                          // implicitly by calling preventDefault on a
                          // nested element, but stopPropagation is added
                          // explicitly too so clicking it can never also
                          // toggle the collapse state in the same
                          // gesture.
                          const isCollapsed = collapsedGroups.has(params.group);
                          const ChevronIcon = isCollapsed
                            ? ChevronRightIcon
                            : ExpandMoreIcon;
                          const toggleCollapse = () => {
                            setCollapsedGroups((prev) => {
                              const next = new Set(prev);
                              if (next.has(params.group)) {
                                next.delete(params.group);
                              } else {
                                next.add(params.group);
                              }
                              return next;
                            });
                          };
                          return (
                            <li key={params.key}>
                              <Box
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  toggleCollapse();
                                }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  px: 1,
                                  py: 0.5,
                                  backgroundColor: "#f5f5f5",
                                  fontWeight: 700,
                                  fontSize: "0.8rem",
                                  cursor: "pointer",
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <ChevronIcon fontSize="small" />
                                  <span>{params.group}</span>
                                </Box>
                                {canSelectAll && (
                                  <Button
                                    size="small"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSelectAllInGroup(params.group);
                                    }}
                                    sx={{
                                      textTransform: "none",
                                      fontSize: "0.75rem",
                                      minWidth: 0,
                                      py: 0,
                                    }}
                                  >
                                    Select All
                                  </Button>
                                )}
                              </Box>
                              {/* ASTT-7319 follow-up: fixed "li cannot
                                  appear as a descendant of li" DOM
                                  nesting warning -- params.children are
                                  already <li> elements (from
                                  renderOption above), so they need a
                                  <ul> wrapper here, not to sit directly
                                  inside this group's own <li>. Matches
                                  MUI's own documented groupBy/renderGroup
                                  pattern (a <ul> housing the group's
                                  items, nested inside the group's <li>),
                                  which this had deviated from. */}
                              {!isCollapsed && (
                                <ul style={{ padding: 0, margin: 0 }}>
                                  {params.children}
                                </ul>
                              )}
                            </li>
                          );
                        }}
                        disableCloseOnSelect
                        PopperComponent={(props) => (
                          <Popper
                            {...props}
                            anchorEl={boxRef.current} 
                            placement="bottom-start"
                            modifiers={[
                              { name: "offset", options: { offset: [0, 8] } },
                            ]}
                            style={{
                              width: boxRef.current?.offsetWidth || 500,
                            }}
                          />
                        )}
                        componentsProps={{
                          paper: {
                            sx: {
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            },
                          },
                        }}
                        renderTags={(value, getTagProps) => {
                          // ASTT-7319: was 1 -- only ever showed a single
                          // chip before collapsing to "+N", confirmed
                          // too aggressive via testing.
                          // ASTT-7319 follow-up: reverted from
                          // MAX_SELECTED_ROSTERS back to its own fixed
                          // value of 4, per direct request -- tying it to
                          // the selection cap (20) meant "+N" could never
                          // actually trigger, since the display limit and
                          // the selection cap were the same number. A
                          // separate display limit is exactly the point
                          // now that the cap itself is much higher: all
                          // 20 chips inline would be visually
                          // overwhelming regardless of the cap's own
                          // value.
                          const limitTags = 4;
                          const more = value.length - limitTags;
                          const tooltipText = value
                            .map((v) => v.rosterLabel)
                            .join(", ");
                          return (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <Tooltip title={tooltipText} arrow>
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  {value
                                    .slice(0, limitTags)
                                    .map((option, index) => {
                                      // ASTT-7319 follow-up: key extracted
                                      // from getTagProps' own return value
                                      // and passed directly -- that object
                                      // already includes its own internal
                                      // key (MUI's Autocomplete generates
                                      // it), so spreading it via
                                      // {...getTagProps({index})} still
                                      // triggered the "key prop spread
                                      // into JSX" warning regardless of
                                      // the separate, redundant explicit
                                      // key={option.rosterId} that used to
                                      // sit above it -- React checks the
                                      // spread object's own contents, not
                                      // just whether a key eventually gets
                                      // assigned.
                                      const { key, ...tagProps } = getTagProps({ index });
                                      // ASTT-7178: same color-coding as
                                      // renderOption below, applied to the
                                      // chip itself so an already-selected
                                      // roster's status is visible without
                                      // reopening the dropdown. Shared
                                      // helper (rosterStatusColors.js) reads
                                      // whichever field matches the active
                                      // domain (handOffStatus/dtrStatus) --
                                      // same lookup Training Notes will use
                                      // once its own tab wires this in.
                                      const statusBackground = getRosterStatusBackground(activeStatusMode, option);
                                      return (
                                        <StyledChip
                                          key={key}
                                          // ASTT-7319: was label={option.rosterLabel}
                                          // -- the full "1235 - JPMC - CAP -
                                          // 65.03 - (Trainer) - (IP)" string,
                                          // piling up illegibly once several
                                          // rosters are selected. Just the ID
                                          // is enough once selected -- the
                                          // full detail is still what's
                                          // searched/shown in the dropdown
                                          // list itself, unaffected by this.
                                          label={option.rosterId}
                                          size="small"
                                          sx={
                                            statusBackground
                                              ? { backgroundColor: statusBackground }
                                              : undefined
                                          }
                                          {...tagProps}
                                        />
                                      );
                                    })}
                                </span>
                              </Tooltip>
                              {more > 0 && (
                                <LimitChip label={`+${more}`} size="small" />
                              )}
                            </span>
                          );
                        }}
                        renderOption={(props, option, { selected }) => {
                          const { key, ...optionProps } = props;
                          const SelectionIcon = selected
                            ? CheckBoxIcon
                            : CheckBoxOutlineBlankIcon;
                          // ASTT-7178: color-coded by this roster's current
                          // status for whichever domain is active -- only
                          // while a status mode is active at all; Roster/
                          // Attendance tabs always see the plain default
                          // look, since coloring is meaningless there.
                          // Shared helper (rosterStatusColors.js) reads
                          // option.handOffStatus or option.dtrStatus
                          // depending on activeStatusMode -- both fields
                          // come straight from filterRosterData/
                          // fetchRosterData, no separate lookup map.
                          const statusBackground = getRosterStatusBackground(activeStatusMode, option);
                          return (
                            <li
                              key={key}
                              {...optionProps}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "6px 12px",
                                backgroundColor: statusBackground,
                              }}
                            >
                              <SelectionIcon
                                fontSize="small"
                                style={{ color: "#3299FF", flexShrink: 0 }}
                              />
                              {option.rosterLabel || ""}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                maxHeight: 45,
                                flexWrap: "nowrap",
                                alignItems: "center",
                                // ASTT-7319 follow-up: widened from 14px --
                                // that wasn't enough dedicated room for the
                                // clear (x) button once several roster
                                // chips were present, letting them visually
                                // overlap it. 36px comfortably fits the
                                // button's own width plus a little
                                // breathing room, confirmed against MUI's
                                // own default endAdornment sizing.
                                paddingRight: "36px !important",
                                "& fieldset": { border: "none" },
                              },
                              "& .MuiInputBase-input": {
                                minWidth: "60px !important",
                                // ASTT-7319 follow-up: lets the actual text
                                // input shrink first when space is tight,
                                // rather than the chips -- chips should
                                // stay fully visible (that's the whole
                                // point of the 4-chip limitTags cap just
                                // above), the search input is what should
                                // give way if the row is crowded.
                                flexShrink: 1,
                              },
                              // Autocomplete's own endAdornment (clear +
                              // dropdown arrow) -- explicit position
                              // keeps it pinned to the input's own right
                              // edge regardless of how many chips render
                              // to its left, rather than flowing inline
                              // with them where overlap could recur.
                              "& .MuiAutocomplete-endAdornment": {
                                position: "absolute",
                                right: "9px",
                              },
                            }}
                            size="small"
                            placeholder="Search"
                          />
                        )}
                      />
                    </Box>
                  </Grid>                 
                </Grid>
              ) : (
                <ReadOnlyField
                  labelLength={4}
                  fieldLength={8}
                  fieldText={rosterId}
                  labelText={"Roster ID"}
                ></ReadOnlyField>
              )}
            </Grid>
            {/* ASTT-7319: "Selected Roster IDs" removed -- redundant with
                the chips already shown directly in the search box above. */}
          </Grid>
          {/* ASTT-7319: statusFields (Curriculum Status) and dates
              (TTF/Product Training/Nesting/Production Start Date)
              removed entirely -- all covered by the table now. */}
        </Grid>
      </Grid>

      {/* ASTT-7319: one row per selected roster, replacing the previous
          Label:Value display of Wave/Class Type/Class Status/Parent
          Client/Client/Program/Project/Site Location/TTF Day 1 Show/
          Active Head Count/Graduated Head Count/Termed -- rendered
          full-width rather than confined to the old two-column layout,
          since a 22-column table needs the room. Client ID/TTEC ID/
          Equipment Readiness are now table columns too, per testing
          confirming they were still comma-concatenating -- the separate
          readinessFields Grid section that used to render them here has
          been removed entirely, nothing left to keep it for. */}
      {/* ASTT-7319 follow-up: was a Typography with its own separate
          fontWeight/color choices that didn't match FieldLabel's color
          and font-size used everywhere else -- FieldLabel itself
          guarantees zero mismatch. */}
      {/* ASTT-7319 follow-up: mt: 2 adds the requested line space between
          the Class Roster Ids search box above and this label -- there
          was none before. */}
      <Box sx={{ mt: 2 }}>
        <FieldLabel>Roster Details</FieldLabel>
      </Box>
      <ClassRosterDetailsTable rows={perRosterDetails} loading={perRosterLoading} onJumpToDtrDate={onJumpToDtrDate} />
      <Drawer
        anchor={"right"}
        open={panelOpen}
        onClose={handleViewProjects}
        PaperProps={{
          sx: { width: "40%" },
        }}
      >
        <DisplayProjects projectNames={projectNames}></DisplayProjects>
      </Drawer>
    </>
  );
}

DTRClassDetails.propTypes = {
  rosterId: PropTypes.string,
  setNewClassRosterId: PropTypes.func,
  isEditable: PropTypes.bool,
  onClassRequestIdChange: PropTypes.func,
  onJumpToDtrDate: PropTypes.func,
  dtrRefreshKey: PropTypes.number,
  reportType: PropTypes.string,
  isHandOffTabActive: PropTypes.bool,
  dtrDate: PropTypes.string,
  isTrainingNotesTabActive: PropTypes.bool,
  handOffRefreshKey: PropTypes.number,
  onRosterStatusListChange: PropTypes.func,
};
